/**
 * SignupWalkthrough — JVZoo-style 4-step illustrated walkthrough of the
 * theailedger.ai signup flow. Lives on the homepage between
 * EcosystemHowItWorks and the category tabs.
 *
 * Why illustrated mocks instead of screenshots:
 *   - Static screenshots go stale every time AuthModal.js changes.
 *   - Mocks built with the same Tailwind classes as the live modal
 *     auto-stay in sync visually.
 *   - No huge .png/.gif assets to load; renders in <1 KB of JSX.
 *
 * Visual language: dark cards on the existing black bg, each step has a
 * big translucent step-number behind the mock + a yellow animated arrow
 * pointing at the specific UI element the visitor needs to click. Same
 * arrow-callout idea as JVZoo's homepage Step01.gif → Step04.gif.
 *
 * Accessibility / motion: arrow pulse uses `prefers-reduced-motion` so
 * users who've opted out of motion get a static arrow.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, User, Store, CheckCircle, Mail, Sparkles,
} from 'lucide-react';
import { Button } from './ui/button';

// Reusable yellow callout arrow. `position` is an absolute-position
// classname so each step can point it at the right element of its mock.
function CalloutArrow({ position, label }) {
  return (
    <div
      className={`absolute z-20 pointer-events-none ${position} signup-walkthrough-arrow`}
      data-testid="signup-walkthrough-arrow"
    >
      <div className="flex items-center gap-2">
        <span className="bg-[#FFC800] text-black text-[11px] font-extrabold uppercase tracking-wider px-2 py-1 rounded shadow-lg whitespace-nowrap">
          {label}
        </span>
        <ArrowRight className="w-7 h-7 text-[#FFC800] drop-shadow-[0_0_8px_rgba(255,200,0,0.6)]" strokeWidth={3} />
      </div>
    </div>
  );
}

// Big translucent step number behind each mock (same visual trick as
// EcosystemHowItWorks so the two sections feel like siblings).
function StepNumber({ n }) {
  return (
    <div
      aria-hidden="true"
      className="absolute -top-4 -left-2 text-[120px] leading-none font-black text-white/[0.04] select-none pointer-events-none"
    >
      {n}
    </div>
  );
}

// ─── Step 1: Click Get Started ─────────────────────────────────────────
function Step1Mock() {
  return (
    <div className="relative bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 h-[260px] flex items-center justify-center">
      <StepNumber n={1} />
      <div className="relative w-full max-w-[280px]">
        <p className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 text-center mb-3 font-bold">
          theailedger.ai
        </p>
        <h4 className="text-white text-lg font-extrabold text-center leading-tight mb-4">
          The AI marketplace<br />for the next generation
        </h4>
        <div className="flex flex-col gap-2 items-center">
          {/* This is the button the arrow points at */}
          <div
            className="relative bg-[#00F0FF] text-black font-bold text-sm px-6 py-3 rounded-lg shadow-lg shadow-[#00F0FF]/30"
            id="signup-walkthrough-target-1"
          >
            Get Started Free
          </div>
          <span className="text-[10px] text-neutral-500">No credit card required</span>
        </div>
      </div>
      <CalloutArrow position="top-1/2 -right-2 -translate-y-1/2" label="Click here" />
    </div>
  );
}

// ─── Step 2: Pick your account type ───────────────────────────────────
function Step2Mock() {
  return (
    <div className="relative bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 h-[260px] flex items-center justify-center">
      <StepNumber n={2} />
      <div className="relative w-full">
        <p className="text-xs font-semibold text-white mb-3">I want to...</p>
        <div className="grid grid-cols-2 gap-2">
          <div className="p-3 rounded-lg border-2 border-[#00F0FF] bg-[#00F0FF]/10">
            <User className="w-5 h-5 mx-auto mb-1.5 text-[#00F0FF]" />
            <p className="font-semibold text-[11px] text-center text-white">Buy Products</p>
          </div>
          <div className="p-3 rounded-lg border-2 border-white/10">
            <Store className="w-5 h-5 mx-auto mb-1.5 text-neutral-500" />
            <p className="font-semibold text-[11px] text-center text-neutral-400">Sell Products</p>
          </div>
        </div>
        <p className="text-[10px] text-neutral-500 mt-3 text-center">
          Or apply as a <span className="text-[#00F0FF]">Partner</span> to be featured
        </p>
      </div>
      <CalloutArrow position="top-1/2 -right-2 -translate-y-1/2" label="Pick one" />
    </div>
  );
}

// ─── Step 3: Confirm email ────────────────────────────────────────────
function Step3Mock() {
  return (
    <div className="relative bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 h-[260px] flex items-center justify-center">
      <StepNumber n={3} />
      <div className="relative w-full max-w-[280px]">
        <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 mb-3">
          <div className="flex items-center gap-2 mb-2">
            <Mail className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-[11px] uppercase tracking-wider font-bold text-neutral-400">
              From: theailedger
            </span>
          </div>
          <p className="text-sm font-bold text-white mb-1">Welcome — confirm your email</p>
          <p className="text-[11px] text-neutral-500 mb-3 leading-relaxed">
            Click the button to verify and you're in. Takes ~5 seconds.
          </p>
          <div className="bg-[#00F0FF] text-black text-[11px] font-bold px-3 py-1.5 rounded inline-block">
            Confirm email →
          </div>
        </div>
        <p className="text-[10px] text-neutral-500 text-center">
          Check spam if you don't see it in 1 min
        </p>
      </div>
      <CalloutArrow position="top-[42%] -right-2 -translate-y-1/2" label="Confirm" />
    </div>
  );
}

// ─── Step 4: You're in — start exploring ──────────────────────────────
function Step4Mock() {
  return (
    <div className="relative bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 h-[260px] flex items-center justify-center">
      <StepNumber n={4} />
      <div className="relative w-full max-w-[280px]">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#00FF94] flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-black" />
          </div>
          <div className="flex-1">
            <p className="text-[11px] text-neutral-500">Welcome back,</p>
            <p className="text-sm font-bold text-white">You're in 🎉</p>
          </div>
        </div>
        <div className="space-y-1.5 mb-3">
          <div className="flex items-center gap-2 text-[11px] text-neutral-300">
            <CheckCircle className="w-3.5 h-3.5 text-[#00FF94]" />
            52 AI categories to browse
          </div>
          <div className="flex items-center gap-2 text-[11px] text-neutral-300">
            <CheckCircle className="w-3.5 h-3.5 text-[#00FF94]" />
            Verified creator products
          </div>
          <div className="flex items-center gap-2 text-[11px] text-neutral-300">
            <CheckCircle className="w-3.5 h-3.5 text-[#00FF94]" />
            40+ featured AI partners
          </div>
        </div>
        <div className="bg-[#00F0FF] text-black text-[11px] font-bold px-3 py-1.5 rounded text-center">
          Start exploring →
        </div>
      </div>
      <CalloutArrow position="bottom-6 right-4" label="That's it" />
    </div>
  );
}

const STEPS = [
  { n: 1, title: 'Click Get Started', sub: 'On any "Get Started" or "Sign In" button. No credit card.', Mock: Step1Mock },
  { n: 2, title: 'Pick your path', sub: 'Buyer to browse, Creator to sell. Switch later anytime.', Mock: Step2Mock },
  { n: 3, title: 'Confirm your email', sub: 'One-click verification link lands in your inbox in ~5 sec.', Mock: Step3Mock },
  { n: 4, title: "You're in", sub: 'Start browsing 52 categories, save tools, follow creators.', Mock: Step4Mock },
];

export default function SignupWalkthrough() {
  const navigate = useNavigate();
  return (
    <section
      className="container mx-auto px-4 md:px-12 py-12 md:py-16 border-t border-white/10"
      data-testid="signup-walkthrough-section"
    >
      <style>{`
        @keyframes signup-arrow-bob {
          0%, 100% { transform: translateY(-50%) translateX(0); }
          50%      { transform: translateY(-50%) translateX(6px); }
        }
        .signup-walkthrough-arrow { animation: signup-arrow-bob 1.4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .signup-walkthrough-arrow { animation: none; }
        }
      `}</style>

      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="text-[11px] tracking-[0.14em] uppercase font-bold text-[#00F0FF] mb-2">
          Sign up in 4 steps
        </div>
        <h2 className="text-3xl md:text-4xl font-extrabold mb-3">
          Get started in under 60 seconds
        </h2>
        <p className="text-neutral-400 text-base leading-relaxed">
          No credit card, no demo call. Just pick how you want to use theailedger.ai and you're in.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        {STEPS.map(({ n, title, sub, Mock }) => (
          <div key={n} className="flex flex-col gap-3" data-testid={`signup-walkthrough-step-${n}`}>
            <Mock />
            <div className="px-1">
              <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#00F0FF]">Step {n}</p>
              <h3 className="text-white text-base font-bold leading-tight mt-1">{title}</h3>
              <p className="text-neutral-400 text-sm leading-relaxed mt-1">{sub}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-center">
        <Button
          className="bg-[#00F0FF] text-black font-bold hover:bg-[#00F0FF]/90 px-8 py-6 text-base shadow-lg shadow-[#00F0FF]/30"
          onClick={() => navigate('/?signup=1')}
          data-testid="signup-walkthrough-cta"
        >
          Sign up free <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </section>
  );
}
